<script setup>
// NOTICE: KAPTION-INTEGRATION
// security-center.TEST.3 — "Probar la conexión", step 2 of 2 (canvas: SsoTest).
// Opens the IdP in a popup (opened on the click itself so it is not blocked),
// waits for the popup's "done" message (or polls), then shows what the IdP
// sent and how Rumo would use it. Nobody is signed in by the test.
import { computed, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import KaptionSecurityAPI from 'dashboard/api/kaptionSecurity';
import { useKaptionSecurity } from 'dashboard/composables/useKaptionSecurity';
import { useKaptionSecurityText } from '../useKaptionSecurityText';

const emit = defineEmits(['edit']);
const { t } = useI18n();
const { overview, setOverview, fetchOverview, connection } =
  useKaptionSecurity();
const { provider, roleName, reasonText } = useKaptionSecurityText(overview);

const dialogRef = ref(null);
const phase = ref('idle'); // idle | waiting | done
const result = ref(null);
const popupBlocked = ref(false);
const enabling = ref(false);
let popup = null;
let pollTimer = null;
let testUrl = '';

const stopWaiting = () => {
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
  window.removeEventListener('message', onMessage); // eslint-disable-line no-use-before-define
};

const loadResult = async () => {
  try {
    const { data } = await KaptionSecurityAPI.testResult();
    if (!data?.result) return false;
    result.value = data.result;
    phase.value = 'done';
    stopWaiting();
    fetchOverview({ force: true });
    return true;
  } catch (e) {
    return false;
  }
};

function onMessage(event) {
  if (event.origin !== window.location.origin) return;
  if (event.data?.type !== 'kaption-sso-test-done') return;
  loadResult();
}

const openPopup = () => {
  popup = window.open(
    testUrl || 'about:blank',
    'kaption-sso-test',
    'width=520,height=720'
  );
  popupBlocked.value = !popup;
};

const start = async () => {
  result.value = null;
  phase.value = 'waiting';
  testUrl = '';
  openPopup();
  dialogRef.value?.open();
  try {
    const { data } = await KaptionSecurityAPI.startTest();
    testUrl = `${window.location.origin}${data.test_url}`;
    if (popup && !popup.closed) popup.location.href = testUrl;
    window.addEventListener('message', onMessage);
    pollTimer = setInterval(loadResult, 2500);
  } catch (e) {
    popup?.close();
    phase.value = 'idle';
    dialogRef.value?.close();
    useAlert(t('KAPTION_SECURITY.COMMON.ERROR'));
  }
};

onBeforeUnmount(stopWaiting);

const ok = computed(() => !!result.value?.ok);
const rows = computed(() => result.value?.claims || []);
const emailDomain = computed(() => {
  const email = rows.value.find(r => r.key === 'email')?.received || '';
  return email.split('@')[1] || '';
});

const meaning = row => {
  if (!row.meaning) return '';
  return t(`KAPTION_SECURITY.TEST.MEANINGS.${row.meaning}`, {
    domain: emailDomain.value,
    provider: provider.value,
  });
};

const errorMessage = computed(() => {
  const code = result.value?.error_code;
  if (!code) return '';
  const known = Object.keys({
    missing_email: 1,
    email_not_verified: 1,
    signature_invalid: 1,
    issuer_mismatch: 1,
    audience_mismatch: 1,
    token_expired: 1,
    nonce_mismatch: 1,
    token_exchange_failed: 1,
    invalid_response: 1,
    assertion_replayed: 1,
    unsolicited_response: 1,
    encrypted_assertion_unsupported: 1,
  });
  return known.includes(code)
    ? t(`KAPTION_SECURITY.TEST.ERRORS.${code}`, { provider: provider.value })
    : t('KAPTION_SECURITY.TEST.ERRORS.generic', { code });
});

const decisionText = computed(() => {
  const decision = result.value?.decision;
  if (!decision) return '';
  if (decision.ok)
    return decision.jit
      ? t('KAPTION_SECURITY.TEST.DECISION_OK_JIT', {
          role: roleName(decision.role),
        })
      : t('KAPTION_SECURITY.TEST.DECISION_OK', {
          role: roleName(decision.role),
        });
  return t('KAPTION_SECURITY.TEST.DECISION_DENIED', {
    reason: `${decision.code} · ${reasonText(decision.code)}`,
  });
});

const canEnable = computed(
  () => ok.value && connection.value?.status === 'tested'
);

const enable = async () => {
  enabling.value = true;
  try {
    const { data } = await KaptionSecurityAPI.enableConnection();
    setOverview(data);
    useAlert(t('KAPTION_SECURITY.SSO.ENABLED'));
    dialogRef.value?.close();
  } catch (e) {
    useAlert(t('KAPTION_SECURITY.COMMON.ERROR'));
  } finally {
    enabling.value = false;
  }
};

const backToData = () => {
  dialogRef.value?.close();
  emit('edit');
};

const onClose = () => {
  stopWaiting();
  phase.value = 'idle';
};

defineExpose({ start });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('KAPTION_SECURITY.TEST.TITLE')"
    :show-confirm-button="false"
    :show-cancel-button="false"
    width="2xl"
    overflow-y-auto
    @close="onClose"
  >
    <template #description>
      <p class="mb-0 text-sm text-n-slate-11">
        {{
          phase === 'done'
            ? t('KAPTION_SECURITY.TEST.DESC', { provider })
            : t('KAPTION_SECURITY.TEST.WAITING', { provider })
        }}
      </p>
    </template>

    <div
      v-if="phase === 'waiting'"
      class="flex flex-col items-center gap-4 py-6"
    >
      <Spinner />
      <p v-if="popupBlocked" class="m-0 text-sm text-n-amber-11">
        {{ t('KAPTION_SECURITY.TEST.POPUP_BLOCKED') }}
      </p>
      <Button
        faded
        type="button"
        :label="t('KAPTION_SECURITY.TEST.OPEN_AGAIN')"
        @click="openPopup"
      />
    </div>

    <div
      v-else-if="phase === 'done'"
      class="flex flex-col gap-4"
      data-testid="ks-test-result"
    >
      <div
        class="flex items-start gap-2.5 px-3 py-2.5 text-sm rounded-lg"
        :class="
          ok
            ? result.domain_verified
              ? 'bg-n-teal-3 text-n-teal-11'
              : 'bg-n-amber-3 text-n-amber-11'
            : 'bg-n-ruby-3 text-n-ruby-11'
        "
      >
        <span
          class="mt-0.5 size-4 shrink-0"
          :class="ok ? 'i-lucide-circle-check' : 'i-lucide-circle-alert'"
          aria-hidden="true"
        />
        <span v-if="!ok">{{ errorMessage }}</span>
        <span v-else-if="result.domain_verified">{{
          t('KAPTION_SECURITY.TEST.OK')
        }}</span>
        <span v-else>{{
          t('KAPTION_SECURITY.TEST.OK_DOMAIN_PENDING', { domain: emailDomain })
        }}</span>
      </div>

      <div
        v-if="rows.length"
        class="overflow-x-auto border rounded-xl border-n-weak"
      >
        <div
          class="grid grid-cols-[130px_minmax(0,1fr)_minmax(0,1fr)] gap-4 px-4 py-2.5 text-xs font-medium uppercase text-n-slate-11 bg-n-alpha-1"
        >
          <span>{{ t('KAPTION_SECURITY.TEST.HEAD_CLAIM') }}</span>
          <span>{{
            t('KAPTION_SECURITY.TEST.HEAD_RECEIVED', { provider })
          }}</span>
          <span>{{ t('KAPTION_SECURITY.TEST.HEAD_RUMO') }}</span>
        </div>
        <div
          v-for="row in rows"
          :key="row.key"
          class="grid grid-cols-[130px_minmax(0,1fr)_minmax(0,1fr)] gap-4 px-4 py-2.5 text-sm border-t border-n-weak"
        >
          <span class="text-n-slate-11">{{
            t(`KAPTION_SECURITY.TEST.CLAIMS.${row.key}`)
          }}</span>
          <code
            class="text-[13px] break-all"
            :class="row.received ? 'text-n-slate-12' : 'text-n-ruby-11'"
            >{{ row.received || t('KAPTION_SECURITY.TEST.NOT_SENT') }}</code
          >
          <span class="text-n-slate-12">{{ meaning(row) }}</span>
        </div>
      </div>

      <p
        v-if="decisionText"
        class="m-0 text-sm text-n-slate-12"
        data-testid="ks-test-decision"
      >
        {{ decisionText }}
      </p>

      <div class="flex flex-wrap justify-end gap-2">
        <Button
          faded
          slate
          type="button"
          :label="t('KAPTION_SECURITY.TEST.BACK')"
          @click="backToData"
        />
        <Button
          v-if="!ok"
          type="button"
          :label="t('KAPTION_SECURITY.TEST.RETRY')"
          @click="start"
        />
        <Button
          v-else-if="canEnable"
          type="button"
          :label="t('KAPTION_SECURITY.TEST.ENABLE')"
          :is-loading="enabling"
          data-testid="ks-test-enable"
          @click="enable"
        />
        <Button
          v-else
          faded
          type="button"
          :label="t('KAPTION_SECURITY.COMMON.CLOSE')"
          @click="dialogRef.close()"
        />
      </div>
      <p v-if="canEnable" class="m-0 text-[13px] text-n-slate-11">
        {{ t('KAPTION_SECURITY.TEST.FOOT') }}
      </p>
    </div>
  </Dialog>
</template>
